import React from "react";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../lib/i18n";
import { useNavigate } from "react-router-dom";
import { GameController, Code, Palette, Cpu, Sparkle, ArrowRight, Globe, MusicNotes, Graph, CircleNotch, Warning, BookOpen, Lightbulb } from "@phosphor-icons/react";

export default function Landing() {
  const { user, loading, error, login, register, devLogin, acceptAuth } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const [isLoggingIn, setIsLoggingIn] = React.useState(false);
  const [mode, setMode] = React.useState("login");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [name, setName] = React.useState("");
  const [formError, setFormError] = React.useState("");
  const [showForm, setShowForm] = React.useState(false);

  React.useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const handleEmailAuth = async (e) => {
    e?.preventDefault();
    setFormError("");
    setIsLoggingIn(true);
    try {
      if (mode === "register") {
        await register({ email, password, name });
      } else {
        await login(email, password);
      }
      navigate("/dashboard");
    } catch (err) {
      setFormError(err?.response?.data?.detail || (lang === "es" ? "No se pudo entrar" : "Could not sign in"));
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogin = async () => {
    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
      setShowForm(true);
      return;
    }
    setIsLoggingIn(true);
    try {
      const doLogin = () => {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID || "",
          callback: async ({ credential }) => {
            try {
              const res = await (await import("../lib/api")).auth.session(credential);
              if (acceptAuth) acceptAuth(res);
              else if (res && res.token) {
                localStorage.setItem("auth_token", res.token);
                const { api } = await import("../lib/api");
                api.defaults.headers.common["Authorization"] = `Bearer ${res.token}`;
              }
              navigate("/dashboard");
            } catch (e) {
              console.error("Login error", e);
              setIsLoggingIn(false);
              setShowForm(true);
            }
          },
          auto_select: false,
        });
        window.google.accounts.id.prompt();
      };

      if (window.google?.accounts?.id) {
        doLogin();
      } else {
        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.defer = true;
        script.onload = doLogin;
        document.head.appendChild(script);
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  const sections = [
    { icon: Lightbulb, t: lang === "es" ? "Ideas modulares" : "Modular ideas", b: lang === "es" ? "Personajes, NPCs, mobs, bosses, armas y más. Estados, etiquetas y subcategorías." : "Characters, NPCs, mobs, bosses, weapons and more. Status, tags and subcategories." },
    { icon: BookOpen, t: lang === "es" ? "Historia y mundo" : "Story & world", b: lang === "es" ? "Capítulos, misiones, secretos y facciones con el mismo sistema de colores." : "Chapters, missions, secrets and factions with the same color system." },
    { icon: Palette, t: "landing.f3.title", b: "landing.f3.body" },
    { icon: MusicNotes, t: lang === "es" ? "Audio" : "Audio", b: lang === "es" ? "SFX, música y ambientación con importación, recorte y recomendaciones." : "SFX, music and ambience with import and recommendations." },
    { icon: Code, t: "landing.f2.title", b: "landing.f2.body" },
    { icon: Graph, t: "Canvas + Kanban", b: lang === "es" ? "Conecta entidades y tareas por etiquetas y módulos." : "Connect entities and tasks with tags and modules." },
    { icon: Cpu, t: "landing.f4.title", b: "landing.f4.body" },
    { icon: Sparkle, t: lang === "es" ? "IA integrada" : "Built-in AI", b: lang === "es" ? "Mecánicas, código, assets, historia y balance." : "Mechanics, code, assets, story and balance." },
  ];

  const Cta = ({ testid }) => (
    <button data-testid={testid} onClick={() => { setShowForm(true); setMode("login"); }} disabled={loading || isLoggingIn} className="gc-btn gc-btn-primary h-12 px-6">
      {isLoggingIn || loading ? <><CircleNotch size={16} className="animate-spin" /> {lang === "es" ? "Preparando..." : "Loading..."}</> : <>{t("landing.cta")} <ArrowRight size={16} /></>}
    </button>
  );

  return (
    <div className="gc-app min-h-screen">
      <header className="sticky top-0 z-40 px-4 pt-4">
        <div className="gc-glass max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5" data-testid="brand-mark">
            <div className="w-9 h-9 rounded-xl bg-sky-500 text-white flex items-center justify-center">
              <GameController size={18} weight="fill" />
            </div>
            <span className="font-display font-bold">{t("app.name")}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setLang(lang === "es" ? "en" : "es")} className="gc-btn gc-btn-ghost h-9 px-3 text-xs"><Globe size={14} /> {lang.toUpperCase()}</button>
            <Cta testid="header-login-btn" />
          </div>
        </div>
        {error && (
          <div className="max-w-5xl mx-auto mt-2 gc-card px-4 py-2 text-xs text-amber-700 flex items-center gap-2">
            <Warning size={14} /> {error}
          </div>
        )}
      </header>

      <section className="max-w-3xl mx-auto px-6 pt-20 pb-16">
        <div className="text-sm mb-4 flex items-center gap-2" style={{ color: "var(--gc-muted)" }}>
          <span className="w-2 h-2 rounded-full bg-sky-500" />
          {lang === "es" ? "Estudio en la nube para juegos 2D" : "Cloud studio for 2D games"}
        </div>
        <h1 className="font-display text-4xl sm:text-6xl font-bold tracking-tight leading-[1.05]">{t("landing.hero.title")}</h1>
        <p className="mt-6 text-base leading-relaxed" style={{ color: "var(--gc-muted)" }}>{t("landing.hero.sub")}</p>
        <div className="mt-10 flex flex-wrap items-center gap-3">
          <Cta testid="hero-cta-btn" />
          <button type="button" className="gc-btn gc-btn-ghost h-12 px-6" onClick={() => { setShowForm(true); setMode("register"); }}>
            {lang === "es" ? "Crear cuenta" : "Create account"}
          </button>
          <div className="text-xs" style={{ color: "var(--gc-muted)" }}>2D · IA · Motor de pruebas · Canvas + Kanban</div>
        </div>

        {showForm && (
          <form onSubmit={handleEmailAuth} className="gc-card mt-8 p-5 space-y-3 max-w-md">
            <div className="flex gap-2 text-xs">
              <button type="button" className={"gc-chip " + (mode === "login" ? "gc-chip-on" : "")} onClick={() => setMode("login")}>
                {lang === "es" ? "Entrar" : "Sign in"}
              </button>
              <button type="button" className={"gc-chip " + (mode === "register" ? "gc-chip-on" : "")} onClick={() => setMode("register")}>
                {lang === "es" ? "Crear cuenta" : "Register"}
              </button>
            </div>
            {mode === "register" && (
              <input className="gc-input h-10 w-full px-3" placeholder={lang === "es" ? "Nombre" : "Name"} value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
            )}
            <input className="gc-input h-10 w-full px-3" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
            <input className="gc-input h-10 w-full px-3" type="password" required minLength={8} placeholder={lang === "es" ? "Contraseña (mín. 8)" : "Password (min 8)"} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete={mode === "register" ? "new-password" : "current-password"} />
            {formError && <div className="text-xs text-amber-500">{formError}</div>}
            <button type="submit" disabled={isLoggingIn} className="gc-btn gc-btn-primary h-11 w-full">
              {isLoggingIn ? <CircleNotch size={16} className="animate-spin" /> : (mode === "register" ? (lang === "es" ? "Crear y entrar" : "Create and enter") : (lang === "es" ? "Iniciar sesión" : "Sign in"))}
            </button>
            {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
              <button type="button" className="gc-btn gc-btn-ghost h-10 w-full text-xs" onClick={handleLogin}>Google</button>
            )}
            <button
              type="button"
              className="text-xs underline"
              style={{ color: "var(--gc-muted)" }}
              onClick={async () => {
                setFormError("");
                setIsLoggingIn(true);
                try {
                  await devLogin();
                  navigate("/dashboard");
                } catch {
                  setFormError(lang === "es" ? "Modo local no disponible (DEV_MODE)" : "Local mode unavailable");
                } finally {
                  setIsLoggingIn(false);
                }
              }}
            >
              {lang === "es" ? "Ya tenías proyectos locales — continuar" : "Continue as local user"}
            </button>
          </form>
        )}
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-20 space-y-2">
        {sections.map(({ icon: Icon, t: tk, b }, i) => (
          <div key={i} className="gc-card p-4 flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-sky-500/15 text-sky-600 flex items-center justify-center shrink-0">
              <Icon size={18} weight="duotone" />
            </div>
            <div>
              <div className="font-semibold">{typeof tk === "string" && tk.startsWith("landing.") ? t(tk) : tk}</div>
              <div className="text-sm mt-0.5" style={{ color: "var(--gc-muted)" }}>{typeof b === "string" && b.startsWith("landing.") ? t(b) : b}</div>
            </div>
          </div>
        ))}
      </section>

      <footer className="max-w-5xl mx-auto px-6 py-8 flex justify-between text-xs" style={{ color: "var(--gc-muted)" }}>
        <span>GameCraft 2D © {new Date().getFullYear()}</span>
        <span>{lang === "es" ? "Hecho para indies 2D" : "Built for 2D indies"}</span>
      </footer>
    </div>
  );
}
